/**
 * CallLog - Call Logging, call-first.
 *
 * This tab used to be customer-first: you picked a customer, then saw their calls. That made
 * unmatched calls unreachable BY CONSTRUCTION - a call with no customer had nowhere to appear,
 * so every ownerless transcript the pipeline saved was invisible in the app.
 *
 * Here the call is the row and the customer is a column. Unmatched calls are first-class, and
 * carry the actions that resolve them: attach to an existing customer, or create one.
 *
 * Customer-scoped history still lives on the customer page and is unchanged.
 *
 * Layout uses the existing CRM.css classes so it matches the other CRM tabs; anything new is
 * an inline style object (Tailwind utilities do nothing in this build).
 */

// ==== STANDALONE:FARMHAND_PRO:START ====
// Feature: call-first call log with unidentified-caller resolution.
// Tier: Pro (telephony)
// Dependencies: services/customerCrmAPI, components/InteractionCard
// ============================================

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CallLogRow, CallLogSummary, CustomerCRMBrief, InteractionTask, customerCRMAPI,
} from '../../services/customerCrmAPI';
import InteractionCard from '../../components/InteractionCard';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

const C = {
  text: '#111827',
  muted: '#6b7280',
  border: '#e5e7eb',
  subtle: '#f9fafb',
  primary: '#059669',
  primarySoft: '#ecfdf5',
  warnBg: '#fffbeb',
  warnBorder: '#fcd34d',
  warnText: '#92400e',
};

const secondaryButton: React.CSSProperties = {
  minHeight: 32,
  padding: '5px 12px',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: C.text,
  backgroundColor: '#fff',
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  cursor: 'pointer',
};

type MatchFilter = '' | 'matched' | 'unmatched';

// Repeat calls from one number inside this window are shown as one conversation. Someone
// ringing back two minutes later about the same bag of dog food is not a second enquiry.
// Wide enough to catch a callback, short enough not to swallow a genuinely separate call.
const REPEAT_WINDOW_MINUTES = 30;

/** A follow-up task's title. Stable, because the server dedupes on (interaction, title) -
 *  so clicking twice returns the first task instead of making a second. */
const followUpTitle = (row: CallLogRow): string =>
  `Follow up: ${row.party_name || row.external_number_e164 || 'call'}`;

const formatWhen = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const formatDuration = (seconds?: number | null): string => {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

interface CallGroup {
  key: string;
  calls: CallLogRow[];
}

/**
 * Group repeat calls from one number within a short window into a single conversation.
 *
 * Someone ringing back two minutes later about the same bag of dog food is one conversation,
 * not two enquiries - the same reasoning as grouping transfer legs, but a different signal:
 * transfer legs share a peerId graph, repeat calls just share a number and a moment.
 *
 * Grouping is presentational ONLY. conversation_key means "legs of one physical call" and
 * these genuinely are separate calls, so the data is left honest and only the display merges.
 * Rows arrive newest-first and stay that way; within a group the calls are ordered oldest
 * first, which is the order they happened.
 */
export const groupRepeatCalls = (rows: CallLogRow[]): CallGroup[] => {
  const groups: CallGroup[] = [];
  const windowMs = REPEAT_WINDOW_MINUTES * 60 * 1000;

  rows.forEach((row) => {
    const number = (row.external_number_e164 || '').trim();
    const when = new Date(row.interaction_date).getTime();

    // No number to group on - always its own row.
    if (number && !isNaN(when)) {
      const open = groups.find((g) => {
        const first = g.calls[0];
        if ((first.external_number_e164 || '').trim() !== number) return false;
        // Matched and unmatched calls on one number are still one caller, but a group must
        // not mix an identified customer with an unidentified row - the resolve actions
        // would be ambiguous.
        if (first.is_matched !== row.is_matched) return false;
        return g.calls.some((c) => {
          const t = new Date(c.interaction_date).getTime();
          return !isNaN(t) && Math.abs(t - when) <= windowMs;
        });
      });
      if (open) {
        open.calls.push(row);
        return;
      }
    }
    groups.push({ key: `g-${row.id}`, calls: [row] });
  });

  groups.forEach((g) => g.calls.sort(
    (a, b) => new Date(a.interaction_date).getTime() - new Date(b.interaction_date).getTime()));
  return groups;
};

// ── resolving an unmatched caller ────────────────────────────────────────────

const ResolvePanel: React.FC<{
  row: CallLogRow;
  customers: CustomerCRMBrief[];
  onResolved: () => void;
}> = ({ row, customers, onResolved }) => {
  const [mode, setMode] = useState<'' | 'attach' | 'create'>('');
  const [search, setSearch] = useState('');
  // Pre-filled with RingCentral's own name, so identifying a caller is one confirm.
  // Carrier placeholders (WIRELESS CALLER) are shown on the row but never seeded here.
  const [newName, setNewName] = useState(
    row.name_is_generic ? '' : (row.external_name || ''),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const matches = search.trim().length >= 2
    ? customers.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
        .slice(0, 8)
    : [];

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onResolved();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  if (!mode) {
    // Small secondary buttons on one line. These are subordinate to the summary above them -
    // full-width primary styling made them louder than the content and read as destructive.
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        <button type="button" style={secondaryButton} onClick={() => setMode('attach')}>
          Attach to customer
        </button>
        <button type="button" style={secondaryButton} onClick={() => setMode('create')}>
          Create customer
        </button>
      </div>
    );
  }

  const field: React.CSSProperties = {
    width: '100%', minHeight: 40, padding: '8px 10px', fontSize: '0.9rem',
    border: `1px solid ${C.border}`, borderRadius: 6, marginBottom: 8,
    boxSizing: 'border-box',
  };

  return (
    <div style={{ marginTop: 8, padding: 12, backgroundColor: '#fff',
                  border: `1px solid ${C.border}`, borderRadius: 8 }}>
      {mode === 'attach' ? (
        <>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: C.muted, marginBottom: 6 }}>
            Attach {row.external_number_e164} to an existing customer
          </div>
          <input
            style={field}
            placeholder="Search customers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={busy}
              onClick={() => run(() => customerCRMAPI.attachCustomer(row.id, c.id))}
              style={{
                display: 'block', width: '100%', textAlign: 'left', minHeight: 40,
                padding: '8px 10px', marginBottom: 4, fontSize: '0.88rem',
                backgroundColor: C.subtle, border: `1px solid ${C.border}`,
                borderRadius: 6, cursor: busy ? 'default' : 'pointer',
              }}
            >
              <strong>{c.name}</strong>
              <span style={{ color: C.muted }}>
                {c.city ? ` · ${c.city}` : ''}{c.phone ? ` · ${c.phone}` : ''}
              </span>
            </button>
          ))}
          {search.trim().length >= 2 && matches.length === 0 && (
            <div style={{ fontSize: '0.85rem', color: C.muted, marginBottom: 8 }}>
              No customer matches “{search}”.
            </div>
          )}
        </>
      ) : (
        <>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: C.muted, marginBottom: 6 }}>
            Create a customer for {row.external_number_e164}
          </div>
          <input
            style={field}
            placeholder="Customer name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            disabled={busy || !newName.trim()}
            style={{
              ...secondaryButton,
              minHeight: 36,
              color: '#fff',
              backgroundColor: busy || !newName.trim() ? C.muted : C.primary,
              borderColor: busy || !newName.trim() ? C.muted : C.primary,
            }}
            onClick={() => run(() => customerCRMAPI.createCustomerFromCall(
              row.id, { name: newName.trim() }))}
          >
            {busy ? 'Creating…' : 'Create and attach'}
          </button>
        </>
      )}

      {error && (
        <div style={{ fontSize: '0.85rem', color: '#b91c1c', marginTop: 8 }}>{error}</div>
      )}
      <button
        type="button"
        onClick={() => { setMode(''); setError(null); }}
        style={{
          marginTop: 8, minHeight: 36, padding: '0 12px', fontSize: '0.85rem',
          background: 'none', border: 'none', color: C.muted, cursor: 'pointer',
        }}
      >
        Cancel
      </button>
    </div>
  );
};

/**
 * The work a call generates, actionable from the row.
 *
 * Commitments-to-tasks existed only on the customer page card, which is not where calls get
 * read - and was unreachable entirely for an unidentified caller, since that page is
 * customer-scoped. A follow-up on an unknown number had nowhere to go.
 *
 * Tasks are never created automatically. The AI proposes; a person commits.
 */
const CallActions: React.FC<{ row: CallLogRow; onChanged: () => void }> = ({
  row, onChanged,
}) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<InteractionTask[]>([]);

  const tasks = [...(row.tasks || []), ...created];
  const commitments = (Array.isArray(row.commitments) ? row.commitments : [])
    .map((c) => (typeof c === 'string' ? { text: c, due_date: null } : c))
    .filter((c) => (c?.text || '').trim().length > 0);

  const taskFor = (text: string) =>
    tasks.find((t) => t.title.trim() === text.trim());

  const create = async (key: string, title: string, due: string | null) => {
    setBusy(key);
    setError(null);
    try {
      const res = await customerCRMAPI.createTaskFromCall(row.id, {
        title, due_date: due || null,
      });
      setCreated((prev) => [...prev, res.data]);
      onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not create the task.');
    } finally {
      setBusy(null);
    }
  };

  // C3: nothing to act on and nothing already actioned - render nothing rather than an
  // empty affordance.
  const needsFollowUp = row.follow_up_needed && !taskFor(followUpTitle(row));
  if (!commitments.length && !needsFollowUp && !tasks.length) return null;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}
    >
      {commitments.map((c, idx) => {
        const text = (c.text || '').trim();
        const existing = taskFor(text);
        return (
          <div key={idx} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                                  gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: '0.82rem', color: C.text, flex: 1, minWidth: 180 }}>
              {text}
              {c.due_date && (
                <span style={{ color: '#047857', fontWeight: 600, fontSize: '0.75rem' }}>
                  {' '}(due {c.due_date})
                </span>
              )}
            </span>
            {existing ? (
              /* C3 - already actioned, so it cannot be done twice. */
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#047857' }}>
                {'\u2713'} Task{existing.assigned_to ? ` · ${existing.assigned_to}` : ''}
              </span>
            ) : (
              <button
                type="button"
                disabled={busy === `c${idx}`}
                style={secondaryButton}
                onClick={() => create(`c${idx}`, text, c.due_date || null)}
              >
                {busy === `c${idx}` ? 'Creating…' : '+ Create task'}
              </button>
            )}
          </div>
        );
      })}

      {/* C2 - the badge said work was owed and offered nothing. This does. */}
      {needsFollowUp && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.82rem', color: C.warnText, flex: 1, minWidth: 180 }}>
            This call needs a follow-up and nobody owns it.
          </span>
          <button
            type="button"
            disabled={busy === 'fu'}
            style={secondaryButton}
            onClick={() => create('fu', followUpTitle(row), null)}
          >
            {busy === 'fu' ? 'Creating…' : '+ Create follow-up task'}
          </button>
        </div>
      )}

      {error && (
        <div style={{ fontSize: '0.78rem', color: '#b91c1c', marginTop: 4 }}>{error}</div>
      )}
    </div>
  );
};

// ── page ─────────────────────────────────────────────────────────────────────

const CallLog: React.FC = () => {
  const [rows, setRows] = useState<CallLogRow[]>([]);
  const [summary, setSummary] = useState<CallLogSummary | null>(null);
  const [customers, setCustomers] = useState<CustomerCRMBrief[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const [match, setMatch] = useState<MatchFilter>('');
  const [direction, setDirection] = useState<'' | 'inbound' | 'outbound'>('');
  const [followUp, setFollowUp] = useState(false);
  const [unrouted, setUnrouted] = useState(false);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [includeFragments, setIncludeFragments] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (match) params.match = match;
      if (direction) params.direction = direction;
      if (followUp) params.follow_up = true;
      if (unrouted) params.unrouted = true;
      if (includeInternal) params.include_internal = true;
      if (includeFragments) params.include_fragments = true;
      if (search.trim()) params.search = search.trim();

      const [callsRes, summaryRes] = await Promise.all([
        customerCRMAPI.getCalls(params),
        customerCRMAPI.getCallSummary(),
      ]);
      setRows(callsRes.data.results);
      setSummary(summaryRes.data);
    } catch (err: any) {
      console.error('Failed to load calls:', err);
      setError('Could not load the call log.');
    } finally {
      setLoading(false);
    }
  }, [match, direction, followUp, unrouted, includeInternal, includeFragments, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    customerCRMAPI.getAll({ include_inactive: false })
      .then((r) => setCustomers(r.data.results))
      .catch(() => setCustomers([]));
  }, []);

  const chip = (label: string, value: number, active: boolean, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px',
        minHeight: 36,
        borderRadius: 999,
        fontSize: '0.82rem',
        fontWeight: 600,
        cursor: 'pointer',
        color: active ? '#fff' : C.text,
        backgroundColor: active ? C.primary : '#fff',
        border: `1px solid ${active ? C.primary : C.border}`,
      }}
    >
      {label} <span style={{ opacity: 0.75 }}>{value}</span>
    </button>
  );

  return (
    <div className="crm-page">
      <CRMSubNav />

      <div className="crm-header">
        <h1>Call Log</h1>
      </div>

      {summary && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {chip('All calls', summary.total, match === '' && !followUp && !unrouted,
                () => { setMatch(''); setFollowUp(false); setUnrouted(false); })}
          {chip('Unmatched', summary.unmatched, match === 'unmatched',
                () => { setMatch(match === 'unmatched' ? '' : 'unmatched'); })}
          {chip('Follow-up', summary.follow_up, followUp,
                () => setFollowUp(!followUp))}
          {chip('Needs an owner', summary.unrouted, unrouted,
                () => setUnrouted(!unrouted))}
        </div>
      )}

      <div className="filters-bar">
        <div className="filter-group" style={{ flex: 1, minWidth: 200 }}>
          <label>Search</label>
          <input
            className="filter-select"
            placeholder="Customer, number, or transcript…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Direction</label>
          <select className="filter-select" value={direction}
                  onChange={(e) => setDirection(e.target.value as any)}>
            <option value="">Any</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>
        </div>

        <div className="filter-group checkbox-filter">
          <label>
            <input type="checkbox" checked={includeInternal}
                   onChange={(e) => setIncludeInternal(e.target.checked)} />
            Include internal calls
          </label>
        </div>

        <div className="filter-group checkbox-filter">
          <label>
            <input type="checkbox" checked={includeFragments}
                   onChange={(e) => setIncludeFragments(e.target.checked)} />
            Include announcement-only
          </label>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading calls…</div>
      ) : error ? (
        <div className="error-banner">{error}</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <h3>No calls match these filters</h3>
          <p>Try clearing the filters, or including internal and announcement-only calls.</p>
        </div>
      ) : (
        <div>
          {groupRepeatCalls(rows).map((group) => {
            const repeat = group.calls.length > 1;
            const first = group.calls[0];
            const spanMinutes = repeat
              ? Math.round(
                  (new Date(group.calls[group.calls.length - 1].interaction_date).getTime()
                   - new Date(first.interaction_date).getTime()) / 60000)
              : 0;
            return (
              <div
                key={group.key}
                style={repeat ? {
                  border: `1px solid ${first.is_matched ? C.border : C.warnBorder}`,
                  borderLeft: `4px solid ${C.primary}`,
                  borderRadius: 8,
                  padding: 8,
                  marginBottom: 10,
                  backgroundColor: C.subtle,
                } : undefined}
              >
                {repeat && (
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: C.text,
                                padding: '2px 4px 8px' }}>
                    {group.calls.length} calls from{' '}
                    {first.external_name || first.external_number_e164}
                    <span style={{ fontWeight: 400, color: C.muted }}>
                      {' '}· within {spanMinutes || 1} min · one conversation
                    </span>
                  </div>
                )}
                {group.calls.map((row) => {
            const open = expanded === row.id;
            return (
              <div
                key={row.id}
                style={{
                  border: `1px solid ${row.is_matched ? C.border : C.warnBorder}`,
                  backgroundColor: row.is_matched ? '#fff' : C.warnBg,
                  borderRadius: 8,
                  marginBottom: 10,
                  overflow: 'hidden',
                }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpanded(open ? null : row.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpanded(open ? null : row.id);
                    }
                  }}
                  style={{ padding: 12, cursor: 'pointer', minHeight: 44 }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline',
                                gap: 10, marginBottom: 4 }}>
                    <span style={{ fontSize: '0.8rem', color: C.muted, minWidth: 110 }}>
                      {formatWhen(row.interaction_date)}
                    </span>

                    {row.is_matched && row.party_type === 'customer' ? (
                      <Link
                        to={`/crm/customers/${row.party_id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontWeight: 700, color: C.text }}
                      >
                        {row.party_name}
                      </Link>
                    ) : (
                      <span style={{ fontWeight: 700,
                                     color: row.is_matched ? C.text : C.warnText }}>
                        {row.party_name}
                        {/* RingCentral's name is the headline; the number becomes secondary
                            detail rather than the only thing we can show. */}
                        {row.external_name && row.external_number_e164
                          && row.party_name !== row.external_number_e164 && (
                          <span style={{ marginLeft: 6, fontSize: '0.78rem', fontWeight: 400,
                                         color: C.muted }}>
                            {row.external_number_e164}
                          </span>
                        )}
                        {!row.is_matched && (
                          <span style={{ marginLeft: 8, fontSize: '0.72rem', fontWeight: 600,
                                         padding: '2px 8px', borderRadius: 999,
                                         backgroundColor: '#fff',
                                         border: `1px solid ${C.warnBorder}` }}>
                            Unidentified
                          </span>
                        )}
                      </span>
                    )}

                    <span style={{ fontSize: '0.78rem', color: C.muted }}>
                      {row.call_direction === 'inbound' ? 'Inbound'
                        : row.call_direction === 'outbound' ? 'Outbound' : '—'}
                      {' · '}{formatDuration(row.duration_seconds)}
                    </span>

                    {row.follow_up_needed && (
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px',
                                     borderRadius: 999, color: C.warnText,
                                     backgroundColor: C.warnBg,
                                     border: `1px solid ${C.warnBorder}` }}>
                        Follow-up{!row.follow_up_notified_at ? ' · needs an owner' : ''}
                      </span>
                    )}

                    {row.handled_by ? (
                      <span style={{ fontSize: '0.78rem', color: C.muted }}>
                        handled by <strong style={{ color: C.text }}>{row.handled_by}</strong>
                      </span>
                    ) : row.handled_by_inferred ? (
                      <span style={{ fontSize: '0.78rem', color: C.muted }}>
                        likely <strong style={{ color: C.text }}>{row.handled_by_inferred}</strong>
                      </span>
                    ) : null}

                    <span style={{ marginLeft: 'auto', fontSize: '0.78rem', fontWeight: 600,
                                   color: C.primary }}>
                      {open ? 'Hide ▲' : 'Open ▼'}
                    </span>
                  </div>

                  <div style={{ fontSize: '0.875rem', color: C.text, lineHeight: 1.45,
                                overflowWrap: 'anywhere' }}>
                    {(row.summary || row.notes || '').trim()
                      || <span style={{ color: C.muted, fontStyle: 'italic' }}>
                           No summary recorded.
                         </span>}
                  </div>

                  {/* C1/C2/C3 - the Call Log is where calls are actually read, so the work
                      a call generates has to be actionable HERE, including on unidentified
                      callers, whose follow-ups previously had nowhere to go at all. */}
                  <CallActions row={row} onChanged={load} />

                  {!row.is_matched && (row.id === group.calls[group.calls.length - 1].id) && (
                    // One resolve panel per group, on the most recent call. Attaching
                    // re-attributes every unmatched call on that number, so offering it
                    // per-row would imply a choice that does not exist.
                    <ResolvePanel row={row} customers={customers} onResolved={load} />
                  )}
                </div>

                {open && (
                  <div style={{ borderTop: `1px solid ${C.border}`, padding: 12,
                                backgroundColor: '#fff' }}>
                    <InteractionCard interaction={row} />
                  </div>
                )}
              </div>
            );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CallLog;
// ==== STANDALONE:FARMHAND_PRO:END ====
